import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import assessmentService from '../../services/assessmentService';
import { Plus, Calendar, BookOpen, Clock, CheckCircle, AlertCircle, FileText, ChevronRight, Trash2, Search } from 'lucide-react';
import AssessmentTimelineModal from './components/AssessmentTimelineModal';
import MarksEntryModal from './components/MarksEntryModal';

const isAcademicSemesterOpen = (semester) => {
  if (!semester || (semester.is_active !== true && String(semester.calendar_status || '').toLowerCase() !== 'active')) return false;
  if (!semester.end_date) return true;
  const end = new Date(`${semester.end_date}T23:59:59`);
  return !Number.isNaN(end.getTime()) && new Date() <= end;
};

const findCalendarPeriod = (academicSemester, calendarSemesters = []) => {
  if (!academicSemester) return null;
  const exactDates = calendarSemesters.find(period =>
    String(period.start_date || '') === String(academicSemester.start_date || '') &&
    String(period.end_date || '') === String(academicSemester.end_date || '')
  );
  if (exactDates) return exactDates;
  // Legacy academic periods can use intake-relative semester numbers, so the
  // date range is a stronger link to the institution Calendar than the old
  // `semester_1`/`semester_2` value. Prefer the Calendar period with the
  // largest overlap and only use the term number as a tie-breaker.
  const overlapping = calendarSemesters
    .filter(period => {
      const calendarStart = new Date(period.start_date);
      const calendarEnd = new Date(period.end_date);
      const academicStart = new Date(academicSemester.start_date);
      const academicEnd = new Date(academicSemester.end_date);
      return [calendarStart, calendarEnd, academicStart, academicEnd].every(date => !Number.isNaN(date.getTime()))
        && calendarStart <= academicEnd && calendarEnd >= academicStart;
    })
    .sort((left, right) => {
      const academicStart = new Date(academicSemester.start_date).getTime();
      const academicEnd = new Date(academicSemester.end_date).getTime();
      const overlap = period => Math.max(0, Math.min(new Date(period.end_date).getTime(), academicEnd) - Math.max(new Date(period.start_date).getTime(), academicStart));
      return overlap(right) - overlap(left);
    });
  if (overlapping[0]) return overlapping[0];
  return calendarSemesters.find(period => {
    if (Number(period.term_number) !== Number(academicSemester.semester_number)) return false;
    const calendarStart = new Date(period.start_date);
    const calendarEnd = new Date(period.end_date);
    const academicStart = new Date(academicSemester.start_date);
    const academicEnd = new Date(academicSemester.end_date);
    return [calendarStart, calendarEnd, academicStart, academicEnd].every(date => !Number.isNaN(date.getTime()))
      && calendarStart <= academicEnd && calendarEnd >= academicStart;
  }) || null;
};

const calendarPeriodLabel = (period) => {
  if (!period) return '';
  const name = period.name || period.calendar_name || '';
  const yearName = period.academic_year_name || period.academic_year?.name || '';
  return [name, yearName].filter(Boolean).join(', ');
};

const getTimelineCalendarPeriod = (timeline, calendarSemesters = []) => {
  if (timeline?.calendar_period?.id) {
    return calendarSemesters.find(period => String(period.id) === String(timeline.calendar_period.id)) || timeline.calendar_period;
  }
  return findCalendarPeriod(timeline?.semester, calendarSemesters);
};

const FormativeAssessmentManagement = () => {
  const { user, profile } = useAuth();
  const [rawTimelines, setRawTimelines] = useState([]);
  const [semesterOptions, setSemesterOptions] = useState([]);
  const [academicSemesterOptions, setAcademicSemesterOptions] = useState([]);
  const [calendarSemesters, setCalendarSemesters] = useState([]);
  const [activeSemesterId, setActiveSemesterId] = useState(null);
  const [activeCalendarSemesterId, setActiveCalendarSemesterId] = useState(null);
  const [semesterFilter, setSemesterFilter] = useState('active');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const isHOD = ['head_of_department', 'poe_manager', 'system_administrator'].includes(profile?.primary_role);
  const isTrainer = profile?.primary_role === 'trainer_lecturer' || isHOD; // HODs can also act as trainers

  const [activeTab, setActiveTab] = useState(isHOD ? 'department' : 'my_units');
  
  // Filters for My Assigned Units
  const [myUnitsCamFilter, setMyUnitsCamFilter] = useState('');
  const [myUnitsUnitCodeFilter, setMyUnitsUnitCodeFilter] = useState('');

  // Modals state
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [isMarksModalOpen, setIsMarksModalOpen] = useState(false);
  const [selectedTimeline, setSelectedTimeline] = useState(null);

  useEffect(() => {
    loadTimelines();
    loadSemesterOptions();
    
    // Subscribe to realtime updates
    const unsubscribe = assessmentService.realtime.subscribeToTimelines(() => {
      loadTimelines();
      loadSemesterOptions();
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [profile]);

  const loadSemesterOptions = async () => {
    try {
      const [{ data, error: semesterError }, { data: calendarData, error: calendarError }, { data: yearData, error: yearError }] = await Promise.all([
        supabase
          .from('academic_semesters')
          .select('id, semester_number, semester_type, start_date, end_date, is_active, program_class_id')
          .order('start_date', { ascending: false }),
        supabase
          .from('semesters')
          .select('id, name, term_number, academic_year_id, start_date, end_date, status')
          .order('start_date', { ascending: false }),
        supabase
          .from('academic_years')
          .select('id, name')
      ]);
      if (semesterError) throw semesterError;

      if (calendarError) console.warn('Calendar display names could not be loaded:', calendarError.message);
      if (yearError) console.warn('Academic year display names could not be loaded:', yearError.message);
      const yearNames = new Map((yearData || []).map(year => [String(year.id), year.name]));
      const calendarRows = (calendarData || []).map(period => ({
        ...period,
        academic_year_name: yearNames.get(String(period.academic_year_id)) || null
      }));
      const academicOptions = (data || []).map(item => {
        const calendarPeriod = findCalendarPeriod(item, calendarRows);
        return {
          ...item,
          calendar_name: calendarPeriod?.name || null,
          calendar_year_name: calendarPeriod?.academic_year_name || null,
          calendar_status: calendarPeriod?.status || null,
          calendar_semester_id: calendarPeriod?.id || null
        };
      });
      const current = academicOptions.find(item => isAcademicSemesterOpen(item)) || null;
      const activeCalendarPeriod = calendarRows.find(period => isAcademicSemesterOpen({ ...period, calendar_status: period.status })) || null;
      setCalendarSemesters(calendarRows);
      setAcademicSemesterOptions(academicOptions);
      // The institution Calendar is the source of truth for the labels users
      // select. Keep the legacy academic-semester IDs out of this list so
      // values such as `semester_1` can never appear in the UI.
      const calendarOptions = calendarRows.map(period => ({
        ...period,
        id: `calendar:${period.id}`,
        calendar_semester_id: period.id,
        calendar_name: period.name,
        calendar_year_name: period.academic_year_name || null,
        calendar_status: period.status || null,
        is_calendar_option: true
      }));
      // Keep older CAM records filterable even when their legacy academic
      // semester has no matching Calendar row. Give those records a neutral
      // historical label rather than exposing implementation values such as
      // `semester_1`.
      const legacyOptions = academicOptions
        .filter(period => !period.calendar_semester_id)
        .map(period => ({
          ...period,
          legacy_label: `Historical period${period.start_date ? ` · ${period.start_date}${period.end_date ? ` – ${period.end_date}` : ''}` : ''}`,
          is_legacy_option: true
        }));
      setSemesterOptions([...calendarOptions, ...legacyOptions]);
      setActiveSemesterId(current?.id || null);
      setActiveCalendarSemesterId(activeCalendarPeriod?.id || null);
      setSemesterFilter(previous => {
        if (previous === 'active' || previous === 'all') return previous;
        return calendarOptions.some(item => String(item.id) === String(previous)) ? previous : 'active';
      });
    } catch (err) {
      console.warn('Error loading academic semesters for CAM filters:', err);
    }
  };

  const loadTimelines = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = { assessment_type: 'formative' };
      const { data, error } = await assessmentService.timeline.getTimelines(filters);
      
      if (error) throw error;
      setRawTimelines(data || []);
    } catch (err) {
      console.error('Error loading timelines:', err);
      setError('Failed to load assessment timelines');
    } finally {
      setLoading(false);
    }
  };

  const isTimelineClosed = (timeline) => {
    const timelineStatus = String(timeline?.assessment_status || '').toLowerCase();
    if (timeline?.reopened_for_remediation && ['active', 'scheduled'].includes(timelineStatus)) return false;
    const closeDate = timeline.end_date || timeline.submission_deadline;
    if (!closeDate) return false;
    const d = new Date(closeDate);
    d.setHours(23, 59, 59, 999);
    return new Date() > d;
  };

  // Automatically deactivate in database when past close date
  useEffect(() => {
    if (rawTimelines.length > 0) {
      const expiredTimelines = rawTimelines.filter(t => 
        t.assessment_status === 'active' && isTimelineClosed(t)
      );
      if (expiredTimelines.length > 0) {
        const expiredIds = expiredTimelines.map(t => t.id);
        supabase
          .from('assessment_timelines')
          .update({ assessment_status: 'completed' })
          .in('id', expiredIds)
          .then(({ error }) => {
            if (!error) {
              console.log('Automatically deactivated expired assessment windows in database:', expiredIds.length);
            }
          });
      }
    }
  }, [rawTimelines]);

  const getDeletedTimelineIds = () => {
    try {
      return JSON.parse(localStorage.getItem('deleted_timeline_ids') || '[]');
    } catch (e) {
      return [];
    }
  };

  // Displayed timelines: grouped for HOD, sorted from recent to oldest, automatically deactivated
  const displayedTimelines = React.useMemo(() => {
    const deletedIds = getDeletedTimelineIds();
    let periodScoped = rawTimelines.filter(t => {
      if (t.assessment_status === 'archived' || t.assessment_status === 'deleted') return false;
      if (deletedIds.includes(t.id)) return false;
      if (t.timelineIds && t.timelineIds.some(id => deletedIds.includes(id))) return false;
      if (semesterFilter === 'active') {
        const calendarPeriod = getTimelineCalendarPeriod(t, calendarSemesters);
        const activeCalendarPeriod = calendarSemesters.find(period => isAcademicSemesterOpen({ ...period, calendar_status: period.status }));
        if (activeCalendarPeriod) return String(t.calendar_semester_id || calendarPeriod?.id || '') === String(activeCalendarPeriod.id);
        return isAcademicSemesterOpen({ ...t.semester, calendar_status: calendarPeriod?.status })
          || (activeSemesterId && String(t.semester_id) === String(activeSemesterId));
      }
      if (semesterFilter.startsWith('calendar:')) {
        const calendarId = semesterFilter.slice('calendar:'.length);
        const calendarPeriod = getTimelineCalendarPeriod(t, calendarSemesters);
        return String(t.calendar_semester_id || calendarPeriod?.id || '') === String(calendarId);
      }
      if (semesterFilter !== 'all') return String(t.semester_id) === String(semesterFilter);
      return true;
    });

    let list = [];
    if (activeTab === 'department' && isHOD) {
      // Group by title, period and dates. The same CAM number starts again
      // for a new semester and must never be merged with an older period.
      const grouped = periodScoped.reduce((acc, curr) => {
        const key = `${curr.title}_${curr.calendar_semester_id || curr.semester_id || 'no-period'}_${curr.start_date}_${curr.end_date}`;
        if (!acc[key]) {
          acc[key] = {
            ...curr,
            isGrouped: true,
            originalTitle: curr.title,
            unitsCount: 1,
            unitIds: [curr.unit_id],
            timelineIds: [curr.id]
          };
        } else {
          acc[key].unitsCount += 1;
          acc[key].unitIds.push(curr.unit_id);
          acc[key].timelineIds.push(curr.id);
        }
        return acc;
      }, {});
      list = Object.values(grouped);
    } else {
      list = periodScoped.map(t => ({ ...t, timelineIds: [t.id] }));
    }

    let activeList = list;

    if (statusFilter !== 'all') {
      activeList = activeList.filter(t => {
        const closed = isTimelineClosed(t);
        const status = String(t.assessment_status || 'active').toLowerCase();
        if (statusFilter === 'open') return !closed && ['active', 'scheduled', 'draft'].includes(status);
        if (statusFilter === 'closed') return closed || ['completed', 'cancelled', 'archived', 'deleted'].includes(status);
        return true;
      });
    }

    if (activeTab === 'my_units') {
      if (myUnitsCamFilter.trim()) {
        const query = myUnitsCamFilter.toLowerCase();
        activeList = activeList.filter(t => t.title?.toLowerCase().includes(query));
      }
      if (myUnitsUnitCodeFilter.trim()) {
        const query = myUnitsUnitCodeFilter.toLowerCase();
        activeList = activeList.filter(t => t.unit?.code?.toLowerCase().includes(query) || t.unit?.name?.toLowerCase().includes(query));
      }
    }

    // Attach automatic deactivation status
    const processed = activeList.map(t => {
      const closed = isTimelineClosed(t);
      const effectiveStatus = closed 
        ? 'closed' 
        : (t.assessment_status || 'active');
      return {
        ...t,
        isClosed: closed,
        effectiveStatus
      };
    });

    // Sort from recent to oldest (newest created or latest start_date first)
    return processed.sort((a, b) => {
      const dateA = new Date(a.created_at || a.start_date || 0);
      const dateB = new Date(b.created_at || b.start_date || 0);
      return dateB - dateA;
    });
  }, [rawTimelines, activeTab, isHOD, myUnitsCamFilter, myUnitsUnitCodeFilter, semesterFilter, activeSemesterId, activeCalendarSemesterId, statusFilter, calendarSemesters]);

  const handleCreateTimeline = () => {
    if (!activeSemesterId && !activeCalendarSemesterId) {
      setError('No active Calendar semester is available. Activate a semester before creating a CAM window.');
      return;
    }
    setSelectedTimeline(null);
    setIsTimelineModalOpen(true);
  };

  const handleEditTimeline = (timeline) => {
    setSelectedTimeline(timeline);
    setIsTimelineModalOpen(true);
  };

  const handleDeleteTimeline = async (timeline) => {
    const idsToDelete = [
      ...(timeline.timelineIds || []),
      timeline.id
    ].filter(Boolean);

    const confirmMessage = timeline.isGrouped
      ? `Are you sure you want to delete "${timeline.title}" across ${timeline.unitsCount} units? This cannot be undone.`
      : `Are you sure you want to delete "${timeline.title}"? This cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);

      // 1. Delete associated student assessments first to prevent FK constraint failures
      try {
        await supabase
          .from('student_assessments')
          .delete()
          .in('timeline_id', idsToDelete);
      } catch (e) {
        console.warn('Could not delete associated student assessments:', e);
      }

      // 2. Physical delete from assessment_timelines
      try {
        await supabase
          .from('assessment_timelines')
          .delete()
          .in('id', idsToDelete);

        if (timeline.unitIds && timeline.originalTitle) {
          await supabase
            .from('assessment_timelines')
            .delete()
            .in('unit_id', timeline.unitIds)
            .eq('title', timeline.originalTitle);
        }
      } catch (delErr) {
        console.warn('Physical delete failed, falling back to soft delete:', delErr);
      }

      // 3. Update status in database to archived/deleted
      try {
        await supabase
          .from('assessment_timelines')
          .update({ assessment_status: 'archived' })
          .in('id', idsToDelete);

        if (timeline.unitIds && timeline.originalTitle) {
          await supabase
            .from('assessment_timelines')
            .update({ assessment_status: 'archived' })
            .in('unit_id', timeline.unitIds)
            .eq('title', timeline.originalTitle);
        }
      } catch (archErr) {}

      // 4. Save to deleted IDs cache so it immediately disappears even if RLS blocks DB delete
      try {
        const currentDeleted = getDeletedTimelineIds();
        const updated = [...new Set([...currentDeleted, ...idsToDelete])];
        localStorage.setItem('deleted_timeline_ids', JSON.stringify(updated));
      } catch (lsErr) {}

      // 5. Reload timelines and refresh UI
      await loadTimelines();
    } catch (err) {
      console.error('Error deleting assessment timeline:', err);
      setError(err?.message || 'Failed to delete assessment window');
    } finally {
      setLoading(false);
    }
  };

  const handleEnterMarks = (timeline) => {
    setSelectedTimeline(timeline);
    setIsMarksModalOpen(true);
  };

  const getStatusBadge = (timeline) => {
    if (timeline.effectiveStatus === 'closed' || timeline.isClosed) {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-red-100 text-red-800 border-red-300 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
          Closed (Deactivated)
        </span>
      );
    }
    const status = timeline.assessment_status || 'active';
    if (timeline.reopened_for_remediation && ['active', 'scheduled'].includes(String(status).toLowerCase())) {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-violet-100 text-violet-800 border-violet-200 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-violet-600"></span>
          Reopened for remediation
        </span>
      );
    }
    let colorClass = 'bg-gray-100 text-gray-800 border-gray-200';
    if (status === 'active') colorClass = 'bg-green-100 text-green-800 border-green-200';
    else if (status === 'scheduled') colorClass = 'bg-blue-100 text-blue-800 border-blue-200';
    else if (status === 'completed') colorClass = 'bg-gray-100 text-gray-800 border-gray-200';
    else if (status === 'draft') colorClass = 'bg-yellow-100 text-yellow-800 border-yellow-200';

    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Formative Assessment Management</h1>
          <p className="text-sm text-slate-500 mt-1">Manage continuous assessment windows and capture marks</p>
        </div>
        
        <div className="flex items-center gap-3 w-full md:w-auto">
          {/* Compact Analytics Summary - HOD View */}
          {isHOD && activeTab === 'department' && (
            <div className="hidden lg:flex items-center bg-white rounded-lg border border-slate-200 shadow-sm p-1 h-[42px]">
              <div className="flex items-center gap-2 px-3 border-r border-slate-100" title="Total Assessments">
                <BookOpen className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-semibold text-slate-700">{displayedTimelines.length}</span>
              </div>
              <div className="flex items-center gap-2 px-3 border-r border-slate-100" title="Active Windows">
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span className="text-sm font-semibold text-slate-700">{displayedTimelines.filter(t => t.effectiveStatus === 'active' && !t.isClosed).length}</span>
              </div>
              <div className="flex items-center gap-2 px-3 border-r border-slate-100" title="Closed / Deactivated">
                <Clock className="h-4 w-4 text-red-500" />
                <span className="text-sm font-semibold text-slate-700">{displayedTimelines.filter(t => t.isClosed || t.effectiveStatus === 'closed').length}</span>
              </div>
              <div className="flex items-center gap-2 px-3" title="Scheduled">
                <Calendar className="h-4 w-4 text-amber-500" />
                <span className="text-sm font-semibold text-slate-700">{displayedTimelines.filter(t => t.assessment_status === 'scheduled' && !t.isClosed).length}</span>
              </div>
            </div>
          )}

          {isHOD && (
            <button 
              onClick={handleCreateTimeline}
              disabled={!activeSemesterId && !activeCalendarSemesterId}
              title={activeSemesterId || activeCalendarSemesterId ? 'Create a CAM in the active Calendar semester' : 'Activate a Calendar semester before creating a CAM'}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm transition-colors flex items-center justify-center space-x-2 h-[42px] flex-1 md:flex-none disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              <Plus className="h-4 w-4" />
              <span>Create Window</span>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 flex items-center border border-red-200">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="bg-white shadow-sm border border-slate-200 rounded-xl overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50 flex flex-col lg:flex-row justify-between items-start lg:items-end px-4 pt-2 pb-3 lg:pb-0 gap-3">
          {isHOD ? (
            <div className="flex space-x-1 -mb-px">
              <button
                onClick={() => setActiveTab('department')}
                className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors rounded-t-lg ${
                  activeTab === 'department' 
                    ? 'border-blue-600 text-blue-700 bg-white shadow-[0_-2px_4px_rgba(0,0,0,0.02)]' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
              >
                Department CAM Windows
              </button>
              <button
                onClick={() => setActiveTab('my_units')}
                className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors rounded-t-lg ${
                  activeTab === 'my_units' 
                    ? 'border-blue-600 text-blue-700 bg-white shadow-[0_-2px_4px_rgba(0,0,0,0.02)]' 
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-100'
                }`}
              >
                My Assigned Units
              </button>
            </div>
          ) : (
            <h2 className="text-lg font-semibold text-slate-800 py-3">
              My Assigned Units CAMs
            </h2>
          )}
          
          <div className="flex items-center gap-3 lg:pb-2.5 w-full lg:w-auto">
            <select
              value={semesterFilter}
              onChange={e => setSemesterFilter(e.target.value)}
              className="w-full lg:w-56 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Filter CAMs by academic semester"
            >
              <option value="active">Current active semester</option>
              <option value="all">All semesters</option>
              {semesterOptions.map(semester => (
                <option key={semester.id} value={semester.id}>
                  {calendarPeriodLabel(semester) || semester.legacy_label || 'Calendar period'}{isAcademicSemesterOpen({ ...semester, calendar_status: semester.status }) ? ' · Active' : ' · Historical'}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="w-full lg:w-36 rounded border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Filter CAMs by status"
            >
              <option value="all">All statuses</option>
              <option value="open">Open windows</option>
              <option value="closed">Closed windows</option>
            </select>
            {activeTab === 'my_units' && (
              <div className="flex items-center gap-2 flex-1 lg:flex-initial">
                <div className="relative w-full lg:w-48">
                  <input
                    type="text"
                    placeholder="Filter by CAM"
                    value={myUnitsCamFilter}
                    onChange={e => setMyUnitsCamFilter(e.target.value)}
                    className="w-full pl-8 pr-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <Search className="h-3 w-3 text-slate-400 absolute left-2.5 top-1.5" />
                </div>
                <div className="relative w-full lg:w-48">
                  <input
                    type="text"
                    placeholder="Filter by Unit Code/Name"
                    value={myUnitsUnitCodeFilter}
                    onChange={e => setMyUnitsUnitCodeFilter(e.target.value)}
                    className="w-full pl-8 pr-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <Search className="h-3 w-3 text-slate-400 absolute left-2.5 top-1.5" />
                </div>
              </div>
            )}
            <span className="hidden sm:inline-block text-xs text-slate-500 bg-slate-200/60 px-2.5 py-1 rounded-full font-medium whitespace-nowrap">
              Sorted: Recent to Oldest
            </span>
          </div>
        </div>
        
        {displayedTimelines.length === 0 ? (
          <div className="p-12 text-center">
            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Assessments Found</h3>
            <p className="text-slate-500 max-w-sm mx-auto">
              {isHOD 
                ? "There are currently no formative assessment windows created." 
                : "You don't have any active assessment windows for your assigned units."}
            </p>
            {isHOD && (
              <button
                onClick={handleCreateTimeline}
                className="mt-6 text-blue-600 font-medium hover:text-blue-800"
                disabled={!activeSemesterId && !activeCalendarSemesterId}
              >
                {activeSemesterId || activeCalendarSemesterId ? '+ Create First Assessment Window' : 'Activate a semester to create a CAM window'}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-emerald-700">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold text-white">SN</th>
                  <th className="px-6 py-4 text-left font-semibold text-white">CAM</th>
                  <th className="px-6 py-4 text-left font-semibold text-white">START DATE</th>
                  <th className="px-6 py-4 text-left font-semibold text-white">END DATE</th>
                  <th className="px-6 py-4 text-left font-semibold text-white">STATUS</th>
                  <th className="px-6 py-4 text-left font-semibold text-white">POE DEADLINE</th>
                  <th className="px-6 py-4 text-left font-semibold text-white">POE STATUS</th>
                  <th className="px-6 py-4 text-right font-semibold text-white">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {displayedTimelines.map((timeline, index) => (
                  <tr key={timeline.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-slate-500">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{timeline.title}</div>
                      <div className="text-xs text-slate-500 mt-1 line-clamp-1">{timeline.description}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                        <span className="flex items-center">
                          <BookOpen className="h-3 w-3 mr-1 text-slate-400" />
                          {timeline.isGrouped 
                            ? `Applies to ${timeline.unitsCount} Units` 
                            : `${timeline.unit?.code || 'Unit'}`}
                        </span>
                        <span className="flex items-center">
                          <FileText className="h-3 w-3 mr-1 text-slate-400" />
                          {timeline.total_marks} Marks
                        </span>
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1 text-slate-400" />
                          {calendarPeriodLabel(getTimelineCalendarPeriod(timeline, calendarSemesters)) || (timeline.semester?.start_date ? `Academic period · ${timeline.semester.start_date}` : 'Academic period')}
                          {isAcademicSemesterOpen({ ...(timeline.semester || {}), ...(getTimelineCalendarPeriod(timeline, calendarSemesters) || {}), calendar_status: getTimelineCalendarPeriod(timeline, calendarSemesters)?.status }) ? ' · Active' : ' · Historical'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                      <div>{new Date(timeline.start_date).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                      <div>{new Date(timeline.end_date).toLocaleDateString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(timeline)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1.5 text-slate-400" />
                        {new Date(timeline.submission_deadline || timeline.end_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(timeline.submission_deadline || timeline.end_date) < new Date() && !timeline.reopened_for_remediation ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/10">
                          Closed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/10">
                          Open
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        {isHOD && (
                          <>
                            <button 
                              onClick={() => handleEditTimeline(timeline)}
                              className="px-3 py-1.5 border border-slate-300 text-slate-700 bg-white rounded hover:bg-slate-50 font-medium text-xs transition-colors"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteTimeline(timeline)}
                              className="flex items-center justify-center px-2 py-1.5 border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 rounded font-medium text-xs transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {(!timeline.isGrouped) && (
                          <button 
                            onClick={() => handleEnterMarks(timeline)}
                            disabled={timeline.effectiveStatus === 'closed'}
                            className={`flex items-center justify-center px-3 py-1.5 rounded font-medium text-xs transition-colors ${
                              timeline.effectiveStatus !== 'closed'
                                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                                : 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                            }`}
                          >
                            <span>{timeline.effectiveStatus === 'closed' ? 'Closed' : 'Enter Marks'}</span>
                            <ChevronRight className="h-3 w-3 ml-1" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isTimelineModalOpen && (
        <AssessmentTimelineModal
          isOpen={isTimelineModalOpen}
          onClose={() => setIsTimelineModalOpen(false)}
          timeline={selectedTimeline}
          defaultSemester={academicSemesterOptions.find(item => String(item.id) === String(activeSemesterId)) || null}
          defaultCalendarSemester={calendarSemesters.find(item => String(item.id) === String(activeCalendarSemesterId)) || null}
          calendarSemesters={calendarSemesters}
          onSuccess={loadTimelines}
        />
      )}

      {isMarksModalOpen && selectedTimeline && (
        <MarksEntryModal
          isOpen={isMarksModalOpen}
          onClose={() => setIsMarksModalOpen(false)}
          timeline={selectedTimeline}
          calendarPeriodName={calendarPeriodLabel(getTimelineCalendarPeriod(selectedTimeline, calendarSemesters))}
          calendarPeriod={getTimelineCalendarPeriod(selectedTimeline, calendarSemesters)}
          calendarSemesters={calendarSemesters}
        />
      )}
    </div>
  );
};

export default FormativeAssessmentManagement;
